import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Skeleton,
  FormControlLabel,
  Checkbox,
  Divider,
  Radio,
  RadioGroup,
  LinearProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import VideocamIcon from '@mui/icons-material/Videocam';
import SyncIcon from '@mui/icons-material/Sync';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PlaceIcon from '@mui/icons-material/Place';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReplayIcon from '@mui/icons-material/Replay';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import SportsHockeyIcon from '@mui/icons-material/SportsHockey';
import TerminalIcon from '@mui/icons-material/Terminal';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LinkIcon from '@mui/icons-material/Link';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

import { auth } from '../../firebase';
import { useSeason } from '../../SeasonContext';
import { useRoster } from '../../RosterContext';
import {
  fetchGameVideos,
  syncScheduleVideos,
  fetchIngestionStatus,
  scheduleGameDownload,
  retryGameDownload,
  lookupGamesheetGame,
  triggerManualGamesheetDownload,
} from '../../utils/apiClient';
import {
  GameVideoSummary,
  IngestionStatusResponse,
  Game,
  GamesheetLookupResponse,
} from '../../types';
import { fetchAndProcessData } from '../../utils/hockeyStatsParser';
import { resolveGameScore, getTargetTeamName, isJetsHomeGame } from '../../utils/gameScoreUtils';
import { TaskDebugLogsModal } from './TaskDebugLogsModal';

const isManualGame = (item?: { isManualDownload?: boolean; source?: string } | null): boolean =>
  Boolean(item?.isManualDownload || item?.source === 'manual_gamesheet');

const formatManualStageLabel = (stage?: string, status?: string): string => {
  const s = (stage || '').toLowerCase().trim();
  const st = (status || '').toLowerCase().trim();
  if (s === 'queued' || s === 'scheduled') return 'Job Submitted — Pending Worker Start';
  if (s === 'initialization') return 'Initializing Pipeline...';
  if (s === 'conflict_check') return 'Checking Active Game Schedule...';
  if (s === 'completion_check') return 'Verifying Official GameSheet Score & Events...';
  if (s === 'livebarn_auth') return 'Connecting to LiveBarn & Checking VOD Stream...';
  if (s === 'download_auto') return 'Downloading 1080p Auto-Tracking Feed...';
  if (s === 'download_pano') return 'Downloading Panoramic Feed...';
  if (s === 'milestone_detection') return 'Detecting Period Starts & Scoreboard Clock...';
  if (s === 'goal_detection') return 'Detecting Goals & Aligning GameSheet Events...';
  if (s === 'highlight_clipping') return 'Generating Goal Highlight Clips & Storyboards...';
  if (s === 'waiting_for_stream' || st === 'waiting_for_stream') {
    return 'Waiting for LiveBarn VOD Stream (Auto-Retrying)';
  }
  if (st === 'failed' || s === 'failed') return 'Download Task Failed';
  if (s) return `Processing (${s.replace(/_/g, ' ')})...`;
  if (st === 'processing') return 'Processing Video Pipeline...';
  if (st === 'scheduled') return 'Job Submitted — Queued for Processing';
  return 'Pending Processing';
};

const GameHighlightsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedSeason } = useSeason();
  const { isAdmin, isAuthReady, userProfile } = useRoster();
  const userEmail = (userProfile?.email || auth.currentUser?.email || '').trim().toLowerCase();
  const isSuperAdmin = userEmail === 'bwuest@gmail.com';
  const isUserAdmin = isSuperAdmin || Boolean(isAdmin);
  const canAccess = Boolean(userEmail || isAuthReady);

  // Active Tab: 0 = Downloads, 1 = Queued, 2 = Tasks, 3 = Manual Downloads (Admins only)
  const [activeTab, setActiveTab] = useState<number>(0);

  // Library State (Downloaded videos)
  const [videos, setVideos] = useState<GameVideoSummary[]>([]);
  const [scheduleGames, setScheduleGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [syncing, setSyncing] = useState<boolean>(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  // Queued Tab State
  const [queuedSearch, setQueuedSearch] = useState<string>('');

  // Admin Ingestion Monitor State
  const [ingestionData, setIngestionData] = useState<IngestionStatusResponse | null>(null);
  const [ingestionLoading, setIngestionLoading] = useState<boolean>(false);
  const [ingestionFilter, setIngestionFilter] = useState<string>('all');
  const [ingestionSort, setIngestionSort] = useState<'queue' | 'newest' | 'oldest'>('queue');
  const [ingestionSearch, setIngestionSearch] = useState<string>('');
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [selectedLogGame, setSelectedLogGame] = useState<{ gameId: string; opponent?: string; date?: string } | null>(null);
  const [actionToast, setActionToast] = useState<{ message: string; severity: 'success' | 'info' | 'warning' | 'error' } | null>(null);

  // Manual GameSheet Download State (Tab 3 - Admins only)
  const [manualUrlInput, setManualUrlInput] = useState<string>('');
  const [lookupLoading, setLookupLoading] = useState<boolean>(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupResult, setLookupResult] = useState<GamesheetLookupResponse | null>(null);
  const [selectedTargetTeam, setSelectedTargetTeam] = useState<string>('');
  const [manualSurfaceInput, setManualSurfaceInput] = useState<string>('');
  const [manualRinkNameInput, setManualRinkNameInput] = useState<string>('');
  const [manualSheetNameInput, setManualSheetNameInput] = useState<string>('');
  const [saveRinkMapping, setSaveRinkMapping] = useState<boolean>(true);
  const [showRinkOverride, setShowRinkOverride] = useState<boolean>(false);
  const [manualQueueing, setManualQueueing] = useState<boolean>(false);

  const loadVideos = async (silent: boolean = false) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const data = await fetchGameVideos(selectedSeason?.key);
      const fetchedList: GameVideoSummary[] = Array.isArray(data) ? data : ((data as any)?.videos || []);
      setVideos((prev) => {
        const fetchedIds = new Set(fetchedList.map((v) => v.gameId || v.id));
        const pendingOptimistic = prev.filter(
          (v) => isManualGame(v) && !fetchedIds.has(v.gameId || v.id)
        );
        return [...pendingOptimistic, ...fetchedList];
      });
    } catch (err: any) {
      if (!silent) {
        setError(err.message || 'Failed to load game videos.');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const loadIngestionStatus = async (silent: boolean = false) => {
    if (!silent) {
      setIngestionLoading(true);
    }
    try {
      const data = await fetchIngestionStatus(selectedSeason?.key);
      setIngestionData(data);
    } catch (err: any) {
      if (!silent) {
        setError(err.message || 'Failed to load ingestion status.');
      }
    } finally {
      if (!silent) {
        setIngestionLoading(false);
      }
    }
  };

  useEffect(() => {
    if (isAuthReady && canAccess) {
      loadVideos();
      if (isUserAdmin) {
        loadIngestionStatus();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeason?.key, canAccess, isAuthReady, isUserAdmin]);

  // Load official league schedule games for final scores & outcome resolution
  useEffect(() => {
    if (!selectedSeason?.seasonId || !selectedSeason?.divisionId) return;
    let isMounted = true;
    fetchAndProcessData(selectedSeason.seasonId, selectedSeason.divisionId)
      .then((data) => {
        if (isMounted && data?.fullSchedule) {
          setScheduleGames(data.fullSchedule);
        }
      })
      .catch((err) => {
        console.warn('Failed to load schedule games for highlights score matching:', err);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedSeason?.seasonId, selectedSeason?.divisionId]);

  // Auto-refresh videos & ingestion tasks when a worker is active, queued, or while viewing Manual Downloads
  useEffect(() => {
    if (!isUserAdmin) return;

    const hasActiveIngestionTask = (ingestionData?.games || []).some(
      (g) =>
        g.status === 'processing' ||
        g.lastTaskStatus === 'running' ||
        g.currentStage === 'queued' ||
        (isManualGame(g) && g.status !== 'complete' && g.status !== 'ready')
    );
    const hasActiveVideoTask = videos.some(
      (v) =>
        v.status === 'processing' ||
        v.lastTaskStatus === 'running' ||
        v.currentStage === 'queued' ||
        (isManualGame(v) && v.status !== 'complete' && (v as any).status !== 'ready')
    );

    if (!hasActiveIngestionTask && !hasActiveVideoTask && activeTab !== 3) return;

    const interval = setInterval(() => {
      loadVideos(true);
      loadIngestionStatus(true);
    }, 5000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingestionData?.games, videos, isUserAdmin, activeTab, selectedSeason?.key]);

  const handleSyncSchedule = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const res = await syncScheduleVideos();
      const msg = `Synced schedule: ${res.queuedGamesCount} game(s) queued for automated download.`;
      setSyncMessage(msg);
      setActionToast({ message: msg, severity: 'success' });
      await Promise.all([loadVideos(true), loadIngestionStatus(true)]);
    } catch (err: any) {
      const msg = err.message || 'Failed to sync schedule.';
      setError(msg);
      setActionToast({ message: msg, severity: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  const handleScheduleGame = async (game: { gameId: string; opponent: string; rinkName: string; surfaceId?: number }) => {
    setActionInProgress(game.gameId);
    try {
      const res = await scheduleGameDownload(game.gameId, {
        opponent: game.opponent,
        rinkName: game.rinkName,
        surfaceId: game.surfaceId,
      });
      const timeStr = res?.scheduledFor ? new Date(res.scheduledFor).toLocaleTimeString() : 'in 1m';
      const msg = `Scheduled download task for game vs ${game.opponent} (run time: ${timeStr}).`;
      setSyncMessage(msg);
      setActionToast({ message: msg, severity: 'success' });
      await Promise.all([loadVideos(true), loadIngestionStatus(true)]);
    } catch (err: any) {
      const msg = err.message || 'Failed to schedule game download.';
      setError(msg);
      setActionToast({ message: msg, severity: 'error' });
    } finally {
      setActionInProgress(null);
    }
  };

  const handleRetryFeed = async (gameId: string, feedType: string = 'pano') => {
    setActionInProgress(gameId);
    try {
      const res = await retryGameDownload(gameId, feedType);
      const timeStr = res?.scheduledFor ? new Date(res.scheduledFor).toLocaleTimeString() : 'in 1m';
      const msg = `Dispatched ${feedType.toUpperCase()} retry! Scheduled execution: ${timeStr} (Task: ${res?.taskName || 'queued'}).`;
      setSyncMessage(msg);
      setActionToast({ message: msg, severity: 'success' });
      await Promise.all([loadVideos(true), loadIngestionStatus(true)]);
    } catch (err: any) {
      const msg = err.message || 'Failed to retry feed.';
      setError(msg);
      setActionToast({ message: msg, severity: 'error' });
    } finally {
      setActionInProgress(null);
    }
  };

  const handleLookupGamesheet = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmed = manualUrlInput.trim();
    if (!trimmed) {
      setLookupError('Please enter a GameSheet URL.');
      return;
    }
    setLookupLoading(true);
    setLookupError(null);
    setLookupResult(null);
    try {
      const res = await lookupGamesheetGame(trimmed);
      setLookupResult(res);
      const homeName = res.homeTeam?.name || '';
      const visitorName = res.visitorTeam?.name || '';
      if (homeName.toLowerCase().includes('jets')) {
        setSelectedTargetTeam(homeName);
      } else if (visitorName.toLowerCase().includes('jets')) {
        setSelectedTargetTeam(visitorName);
      } else {
        setSelectedTargetTeam(homeName || visitorName);
      }
      setManualSurfaceInput(res.livebarn?.surfaceId ? String(res.livebarn.surfaceId) : '');
      setManualRinkNameInput(res.livebarn?.rinkName || res.location || '');
      setManualSheetNameInput(res.livebarn?.sheetName || '');
      setShowRinkOverride(!res.livebarn?.resolved);
    } catch (err: any) {
      setLookupError(err.message || 'Failed to lookup GameSheet URL.');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleTriggerManualDownload = async () => {
    if (!lookupResult) return;
    const effectiveSurfaceId = String(manualSurfaceInput || lookupResult.livebarn?.surfaceId || '').trim();
    if (!effectiveSurfaceId) {
      setLookupError('A LiveBarn Surface ID or LiveBarn URL is required to download this game.');
      setShowRinkOverride(true);
      return;
    }
    if (!selectedTargetTeam) {
      setLookupError('Please select a Target Team.');
      return;
    }

    setManualQueueing(true);
    setLookupError(null);
    try {
      const res = await triggerManualGamesheetDownload({
        gamesheetUrl: lookupResult.gamesheetUrl || manualUrlInput.trim(),
        targetTeam: selectedTargetTeam,
        surfaceId: effectiveSurfaceId,
        rinkName: (manualRinkNameInput || lookupResult.livebarn?.rinkName || lookupResult.location || '').trim(),
        sheetName: (manualSheetNameInput || lookupResult.livebarn?.sheetName || '').trim(),
        saveRinkMapping: Boolean(saveRinkMapping && (!lookupResult.livebarn?.resolved || showRinkOverride)),
        seasonKey: selectedSeason?.key,
      });

      // Immediately insert an optimistic placeholder card into state so it shows up at 0ms
      const isTargetHome =
        selectedTargetTeam.toLowerCase().trim() === (lookupResult.homeTeam?.name || '').toLowerCase().trim();
      const fallbackOpponent = isTargetHome
        ? lookupResult.visitorTeam?.name || 'Opponent'
        : lookupResult.homeTeam?.name || 'Opponent';
      const fallbackGameId =
        res?.gameId || lookupResult.gameId || `manual_gs_${lookupResult.seasonId}_${lookupResult.sourceGameId}`;
      const optimisticVideo: GameVideoSummary = res?.video
        ? {
            ...res.video,
            id: res.video.gameId || fallbackGameId,
            gameId: res.video.gameId || fallbackGameId,
            isManualDownload: true,
            source: 'manual_gamesheet',
          }
        : {
            id: fallbackGameId,
            gameId: fallbackGameId,
            isManualDownload: true,
            source: 'manual_gamesheet',
            gamesheetUrl: lookupResult.gamesheetUrl || manualUrlInput.trim(),
            seasonId: lookupResult.seasonId,
            divisionId: lookupResult.divisionId,
            divisionName: lookupResult.divisionName,
            sourceGameId: lookupResult.sourceGameId,
            date: lookupResult.date,
            scheduledStart: lookupResult.scheduledStart,
            scheduledEnd: lookupResult.scheduledEnd || '',
            targetTeam: res?.targetTeam || selectedTargetTeam,
            opponent: res?.opponent || fallbackOpponent,
            homeTeam: lookupResult.homeTeam?.name,
            visitorTeam: lookupResult.visitorTeam?.name,
            homeLogo: lookupResult.homeTeam?.logo,
            visitorLogo: lookupResult.visitorTeam?.logo,
            isHome: res?.isHome !== undefined ? res.isHome : isTargetHome,
            rinkName: (manualRinkNameInput || lookupResult.livebarn?.rinkName || lookupResult.location || 'Ice Rink').trim(),
            sheetName: (manualSheetNameInput || lookupResult.livebarn?.sheetName || 'Main').trim(),
            surfaceId: Number(res?.surfaceId || effectiveSurfaceId) || 0,
            status: 'processing',
            currentStage: 'queued',
            lastTaskStatus: 'running',
            scheduledForFormatted: res?.scheduledForFormatted || 'Pending immediate execution',
            feeds: {
              auto: { status: 'pending' },
              pano: { status: 'pending' },
            },
            finalScore: lookupResult.finalScore
              ? {
                  homeGoals: lookupResult.finalScore.homeGoals,
                  visitorGoals: lookupResult.finalScore.visitorGoals,
                }
              : undefined,
          };

      setVideos((prev) => [
        optimisticVideo,
        ...prev.filter((v) => (v.gameId || v.id) !== optimisticVideo.gameId),
      ]);

      const msg = `Queued manual download for ${optimisticVideo.targetTeam || selectedTargetTeam} vs ${optimisticVideo.opponent || 'Opponent'}!`;
      setSyncMessage(msg);
      setActionToast({ message: msg, severity: 'success' });
      setLookupResult(null);
      setManualUrlInput('');
      await Promise.all([loadVideos(true), loadIngestionStatus(true)]);
    } catch (err: any) {
      const msg = err.message || 'Failed to trigger manual GameSheet download.';
      setLookupError(msg);
      setActionToast({ message: msg, severity: 'error' });
    } finally {
      setManualQueueing(false);
    }
  };

  // Filtered Downloaded Replays (Ready to watch - strictly excludes manual downloads)
  const downloadedVideos = useMemo(() => videos.filter((v) => {
    if (isManualGame(v)) return false;
    const hasAuto = v.feeds?.auto?.status === 'downloaded';
    const hasPano = v.feeds?.pano?.status === 'downloaded';
    return hasAuto || hasPano || v.status === 'complete' || v.status === 'partial' || (v as any).status === 'ready';
  }), [videos]);

  const filteredDownloadedVideos = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const list = downloadedVideos.filter((v) => {
      return (
        v.opponent.toLowerCase().includes(q) ||
        v.rinkName.toLowerCase().includes(q) ||
        v.date.includes(q)
      );
    });

    return [...list].sort((a, b) => {
      const dateA = `${a.date || ''} ${a.scheduledStart || ''}`;
      const dateB = `${b.date || ''} ${b.scheduledStart || ''}`;
      return sortOrder === 'newest'
        ? dateB.localeCompare(dateA)
        : dateA.localeCompare(dateB);
    });
  }, [downloadedVideos, searchQuery, sortOrder]);

  // Filtered Queued Downloads (Pending/scheduled regular games - strictly excludes manual downloads)
  // Sorted by: active/downloading first -> next to download earliest -> furthest out in future last
  const queuedGames = useMemo(() => {
    return (ingestionData?.games || [])
      .filter((g) => {
        if (isManualGame(g)) return false;
        return (
          g.status === 'scheduled' ||
          g.status === 'overrun_delayed' ||
          g.status === 'retrying_pano' ||
          g.status === 'retrying_auto' ||
          g.status === 'processing' ||
          (g.status === 'partial' && Boolean(g.scheduledExecutionTime))
        );
      })
      .sort((a, b) => {
        // 1. Actively downloading ('processing') always on top
        if (a.status === 'processing' && b.status !== 'processing') return -1;
        if (b.status === 'processing' && a.status !== 'processing') return 1;

        // 2. Sort by scheduledExecutionTime ascending (soonest / next to run first)
        const timeA = a.scheduledExecutionTime ? new Date(a.scheduledExecutionTime).getTime() : 0;
        const timeB = b.scheduledExecutionTime ? new Date(b.scheduledExecutionTime).getTime() : 0;

        if (timeA && timeB) {
          return timeA - timeB;
        }
        if (timeA && !timeB) return -1;
        if (!timeA && timeB) return 1;

        // 3. Fallback: game date & time ascending
        const dateA = `${a.date || ''} ${a.scheduledStart || ''}`;
        const dateB = `${b.date || ''} ${b.scheduledStart || ''}`;
        return dateA.localeCompare(dateB);
      });
  }, [ingestionData?.games]);

  const filteredQueuedGames = useMemo(() => {
    const q = queuedSearch.toLowerCase();
    return queuedGames.filter((g) => {
      return (
        g.opponent.toLowerCase().includes(q) ||
        g.rinkName.toLowerCase().includes(q) ||
        g.date.includes(q)
      );
    });
  }, [queuedGames, queuedSearch]);

  // Unified Manual Games (Merges videos + ingestionData so pending, processing, and completed manual games all appear in Tab 3)
  const allManualGames = useMemo(() => {
    const map = new Map<string, GameVideoSummary>();

    videos.forEach((v) => {
      if (!isManualGame(v)) return;
      const gid = v.gameId || v.id;
      if (!gid) return;
      map.set(gid, { ...v, id: gid, gameId: gid });
    });

    (ingestionData?.games || []).forEach((g) => {
      if (!isManualGame(g)) return;
      const gid = g.gameId;
      if (!gid) return;
      const existing = map.get(gid);
      const autoStatus = typeof g.feeds?.auto === 'string' ? g.feeds.auto : existing?.feeds?.auto?.status || 'pending';
      const panoStatus = typeof g.feeds?.pano === 'string' ? g.feeds.pano : existing?.feeds?.pano?.status || 'pending';

      map.set(gid, {
        ...(existing || ({} as GameVideoSummary)),
        id: gid,
        gameId: gid,
        isManualDownload: true,
        source: g.source || existing?.source || 'manual_gamesheet',
        gamesheetUrl: g.gamesheetUrl || existing?.gamesheetUrl,
        seasonId: g.seasonId || existing?.seasonId,
        divisionId: g.divisionId || existing?.divisionId,
        sourceGameId: g.sourceGameId || existing?.sourceGameId,
        date: g.date || existing?.date || '',
        scheduledStart: g.scheduledStart || existing?.scheduledStart || '',
        scheduledEnd: g.scheduledEnd || existing?.scheduledEnd || '',
        targetTeam: g.targetTeam || existing?.targetTeam,
        opponent: g.opponent || existing?.opponent || 'Opponent',
        homeTeam: g.homeTeam || existing?.homeTeam,
        visitorTeam: g.visitorTeam || existing?.visitorTeam,
        homeLogo: g.homeLogo || existing?.homeLogo,
        visitorLogo: g.visitorLogo || existing?.visitorLogo,
        isHome: g.isHome !== undefined ? g.isHome : existing?.isHome,
        rinkName: g.rinkName || existing?.rinkName || 'Ice Rink',
        sheetName: g.sheetName || existing?.sheetName || 'Main',
        surfaceId: g.surfaceId || existing?.surfaceId || 0,
        livebarnUrl: g.livebarnUrl || existing?.livebarnUrl,
        status: (g.rawStatus || g.status || existing?.status || 'scheduled') as any,
        currentStage: g.currentStage || existing?.currentStage,
        lastTaskStatus: g.lastTaskStatus || existing?.lastTaskStatus,
        lastTaskError: g.lastTaskError || existing?.lastTaskError,
        delayReason: g.delayReason || existing?.delayReason,
        scheduledExecutionTime: g.scheduledExecutionTime || existing?.scheduledExecutionTime,
        scheduledForFormatted: g.scheduledForFormatted || existing?.scheduledForFormatted,
        expectedScore: g.expectedScore || existing?.expectedScore,
        finalScore: g.finalScore || existing?.finalScore,
        scoreResult: g.scoreResult || existing?.scoreResult,
        feeds: {
          auto: {
            ...(existing?.feeds?.auto || {}),
            status: autoStatus,
          },
          pano: {
            ...(existing?.feeds?.pano || {}),
            status: panoStatus,
          },
        },
      });
    });

    const list = Array.from(map.values());
    return list.sort((a, b) => {
      const aRunning =
        a.status === 'processing' ||
        a.lastTaskStatus === 'running' ||
        a.currentStage === 'queued' ||
        (a.feeds?.auto?.status !== 'downloaded' && a.feeds?.pano?.status !== 'downloaded');
      const bRunning =
        b.status === 'processing' ||
        b.lastTaskStatus === 'running' ||
        b.currentStage === 'queued' ||
        (b.feeds?.auto?.status !== 'downloaded' && b.feeds?.pano?.status !== 'downloaded');
      if (aRunning && !bRunning) return -1;
      if (!aRunning && bRunning) return 1;
      const dateA = `${a.date || ''} ${a.scheduledStart || ''}`;
      const dateB = `${b.date || ''} ${b.scheduledStart || ''}`;
      return dateB.localeCompare(dateA);
    });
  }, [videos, ingestionData?.games]);

  // Filtered Ingestion Items (Tasks monitor tab)
  const filteredIngestionGames = useMemo(() => {
    const list = (ingestionData?.games || []).filter((g) => {
      const matchesSearch =
        g.opponent.toLowerCase().includes(ingestionSearch.toLowerCase()) ||
        g.rinkName.toLowerCase().includes(ingestionSearch.toLowerCase()) ||
        g.date.includes(ingestionSearch);

      if (!matchesSearch) return false;
      if (ingestionFilter === 'all') return true;
      if (ingestionFilter === 'scheduled') return g.status === 'scheduled';
      if (ingestionFilter === 'ready') return g.status === 'ready' || g.status === 'complete' || g.status === 'partial';
      if (ingestionFilter === 'retrying') return g.status === 'retrying_pano' || g.status === 'retrying_auto' || g.status === 'overrun_delayed';
      if (ingestionFilter === 'no_feed') return g.status === 'no_feed' || !g.hasLivebarn;
      if (ingestionFilter === 'not_scheduled') return g.status === 'not_scheduled';
      return true;
    });

    if (ingestionSort === 'newest') {
      return [...list].sort((a, b) => `${b.date} ${b.scheduledStart || ''}`.localeCompare(`${a.date} ${a.scheduledStart || ''}`));
    }
    if (ingestionSort === 'oldest') {
      return [...list].sort((a, b) => `${a.date} ${a.scheduledStart || ''}`.localeCompare(`${b.date} ${b.scheduledStart || ''}`));
    }

    // Default 'queue' priority sort:
    // Active/queued tasks (processing, retrying, scheduled) come FIRST, ordered by execution time ascending (next to run earliest).
    // Ready/completed games come next, ordered by game date descending (most recently played first).
    // Unscheduled / no feed games come last.
    return [...list].sort((a, b) => {
      const isQueuedA = ['processing', 'retrying_pano', 'retrying_auto', 'overrun_delayed', 'scheduled'].includes(a.status);
      const isQueuedB = ['processing', 'retrying_pano', 'retrying_auto', 'overrun_delayed', 'scheduled'].includes(b.status);

      if (isQueuedA && !isQueuedB) return -1;
      if (!isQueuedA && isQueuedB) return 1;

      if (isQueuedA && isQueuedB) {
        if (a.status === 'processing' && b.status !== 'processing') return -1;
        if (b.status === 'processing' && a.status !== 'processing') return 1;

        const timeA = a.scheduledExecutionTime ? new Date(a.scheduledExecutionTime).getTime() : 0;
        const timeB = b.scheduledExecutionTime ? new Date(b.scheduledExecutionTime).getTime() : 0;
        if (timeA && timeB) return timeA - timeB;
        if (timeA && !timeB) return -1;
        if (!timeA && timeB) return 1;
        return `${a.date} ${a.scheduledStart || ''}`.localeCompare(`${b.date} ${b.scheduledStart || ''}`);
      }

      const isReadyA = ['ready', 'complete', 'partial'].includes(a.status);
      const isReadyB = ['ready', 'complete', 'partial'].includes(b.status);
      if (isReadyA && !isReadyB) return -1;
      if (!isReadyA && isReadyB) return 1;
      if (isReadyA && isReadyB) {
        return `${b.date} ${b.scheduledStart || ''}`.localeCompare(`${a.date} ${a.scheduledStart || ''}`);
      }

      return `${a.date} ${a.scheduledStart || ''}`.localeCompare(`${b.date} ${b.scheduledStart || ''}`);
    });
  }, [ingestionData?.games, ingestionSearch, ingestionFilter, ingestionSort]);

  const summary = ingestionData?.summary;

  if (isAuthReady && !canAccess) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <VideocamIcon color="primary" sx={{ fontSize: 36 }} />
            Game Highlights & Video Center
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Watch full game broadcasts, jump to key period transitions, and download goal highlights.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          {isUserAdmin && (
            <Button
              variant="outlined"
              startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={handleSyncSchedule}
              disabled={syncing}
              size="small"
            >
              {syncing ? 'Scanning...' : 'Sync Schedule'}
            </Button>
          )}

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              loadVideos();
              if (isUserAdmin) {
                loadIngestionStatus();
              }
            }}
            size="small"
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {syncMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSyncMessage(null)}>
          {syncMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs - Only Rendered for Admins */}
      {isUserAdmin && (
        <Paper square elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(_, newVal) => setActiveTab(newVal)} variant="scrollable" scrollButtons="auto">
            <Tab
              icon={<VideocamIcon fontSize="small" />}
              iconPosition="start"
              label={`Downloads (${downloadedVideos.length})`}
            />
            <Tab
              icon={<ScheduleSendIcon fontSize="small" />}
              iconPosition="start"
              label={`Queued (${queuedGames.length})`}
            />
            <Tab
              icon={<PendingIcon fontSize="small" />}
              iconPosition="start"
              label={`Tasks (${summary?.totalGames || ingestionData?.games?.length || 0})`}
            />
            <Tab
              icon={<AddCircleOutlineIcon fontSize="small" />}
              iconPosition="start"
              label={`Manual Downloads (${allManualGames.length})`}
            />
          </Tabs>
        </Paper>
      )}

      {/* TAB 0: DOWNLOADS (Library of completed video recordings - visible to all) */}
      {(activeTab === 0 || !isUserAdmin) && (
        <Box>
          {/* Filters Bar */}
          <Card variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 8, md: 9 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search downloaded replays by opponent, rink, or date (YYYY-MM-DD)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Sort Order"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as 'newest' | 'oldest')}
                >
                  <MenuItem value="newest">Newest First</MenuItem>
                  <MenuItem value="oldest">Oldest First</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Card>

          {loading || !isAuthReady ? (
            <Grid container spacing={3}>
              {Array.from({ length: 6 }).map((_, idx) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`video-skeleton-${idx}`}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Skeleton variant="text" width={140} height={20} />
                        <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: 16 }} />
                      </Box>

                      <Skeleton variant="text" width="65%" height={32} sx={{ mb: 0.5 }} />
                      <Skeleton variant="text" width="50%" height={20} sx={{ mb: 1.5 }} />

                      <Skeleton variant="rounded" width="100%" height={38} sx={{ borderRadius: 1.5, mb: 1.5 }} />

                      <Stack direction="row" spacing={1} sx={{ mt: 'auto', pt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                        <Skeleton variant="rounded" width={75} height={24} sx={{ borderRadius: 16 }} />
                        <Skeleton variant="rounded" width={75} height={24} sx={{ borderRadius: 16 }} />
                        <Skeleton variant="rounded" width={60} height={24} sx={{ borderRadius: 16 }} />
                      </Stack>
                    </CardContent>

                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Skeleton variant="rounded" width="100%" height={36} sx={{ borderRadius: 1 }} />
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : filteredDownloadedVideos.length === 0 ? (
            <Card variant="outlined" sx={{ p: 5, textAlign: 'center' }}>
              <SportsHockeyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="h6" color="text.secondary">
                No game replays downloaded yet.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {isUserAdmin
                  ? 'Switch to the "Queued" tab to view pending games scheduled for download or trigger one manually.'
                  : 'Game replays will appear here once games conclude and broadcasts are downloaded.'}
              </Typography>
            </Card>
          ) : (
            <Grid container spacing={3}>
              {filteredDownloadedVideos.map((video) => {
                const hasAuto = video.feeds?.auto?.status === 'downloaded';
                const hasPano = video.feeds?.pano?.status === 'downloaded';
                const goalsCount = video.goals?.length || video.highlights?.length || 0;
                const scoreResult = resolveGameScore(video, scheduleGames);

                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={video.gameId || video.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                        },
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarMonthIcon sx={{ fontSize: 16 }} />
                            {video.date} • {video.scheduledStart}
                          </Typography>

                          <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                            label={hasAuto && hasPano ? 'Full Replay' : hasAuto ? 'Auto Feed' : 'Pano Feed'}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        </Box>

                        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ lineHeight: 1.25 }}>
                          vs {video.opponent}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                          <PlaceIcon sx={{ fontSize: 16 }} />
                          {video.rinkName} {video.sheetName && `(${video.sheetName})`}
                        </Typography>

                        {scoreResult && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1.5,
                              p: 0.75,
                              px: 1.25,
                              borderRadius: 1.5,
                              bgcolor: (theme) =>
                                theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Chip
                              label={scoreResult.outcomeLabel.toUpperCase()}
                              color={
                                scoreResult.outcome === 'W'
                                  ? 'success'
                                  : scoreResult.outcome === 'L'
                                  ? 'error'
                                  : 'warning'
                              }
                              size="small"
                              sx={{
                                fontWeight: 800,
                                fontSize: '0.72rem',
                                height: 22,
                                letterSpacing: 0.5,
                              }}
                            />
                            <Typography variant="body2" fontWeight={700} color="text.primary">
                              Final: {scoreResult.jetsScore} – {scoreResult.oppScore}
                              {scoreResult.isOvertime ? ' (OT)' : ''}
                            </Typography>
                          </Box>
                        )}

                        <Stack direction="row" spacing={1} sx={{ mt: 'auto', pt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                          {hasAuto && <Chip label="1080p Auto" size="small" variant="filled" />}
                          {hasPano && <Chip label="Panoramic" size="small" variant="filled" />}
                          {goalsCount > 0 && (
                            <Chip
                              label={`${goalsCount} Goal${goalsCount > 1 ? 's' : ''}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      </CardContent>

                      <CardActions sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<PlayCircleOutlineIcon />}
                          onClick={() => navigate(`/highlights/${video.gameId || video.id}`)}
                        >
                          Watch Game & Clips
                        </Button>
                        {isUserAdmin && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<TerminalIcon />}
                            onClick={() => setSelectedLogGame({ gameId: video.gameId || video.id, opponent: video.opponent, date: video.date })}
                            sx={{ minWidth: '80px' }}
                          >
                            Logs
                          </Button>
                        )}
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

      {/* TAB 1: QUEUED DOWNLOADS (Admins only) */}
      {isUserAdmin && activeTab === 1 && (
        <Box>
          <Card variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search queued games by opponent, rink, or date..."
              value={queuedSearch}
              onChange={(e) => setQueuedSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Card>

          {ingestionLoading ? (
            <Grid container spacing={3}>
              {Array.from({ length: 3 }).map((_, idx) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`queued-skeleton-${idx}`}>
                  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Skeleton variant="text" width={140} height={20} />
                        <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: 16 }} />
                      </Box>
                      <Skeleton variant="text" width="60%" height={32} sx={{ mb: 0.5 }} />
                      <Skeleton variant="text" width="45%" height={20} sx={{ mb: 1.5 }} />
                      <Skeleton variant="rounded" width="100%" height={28} sx={{ borderRadius: 1 }} />
                    </CardContent>
                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Skeleton variant="rounded" width="100%" height={36} sx={{ borderRadius: 1 }} />
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : filteredQueuedGames.length === 0 ? (
            <Card variant="outlined" sx={{ p: 5, textAlign: 'center' }}>
              <ScheduleSendIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="h6" color="text.secondary">
                No downloads currently in queue.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                All scheduled games have completed or have not been queued yet.
              </Typography>
            </Card>
          ) : (
            <Grid container spacing={3}>
              {filteredQueuedGames.map((game) => {
                const isBusy = actionInProgress === game.gameId;
                const isTaskRunning = game.status === 'processing' || game.lastTaskStatus === 'running';
                const queuedScore = resolveGameScore(game as any, scheduleGames);

                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={game.gameId}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarMonthIcon sx={{ fontSize: 16 }} />
                            {game.date} • {game.scheduledStart}
                          </Typography>

                          {isTaskRunning ? (
                            <Chip
                              icon={<CircularProgress size={12} />}
                              label={game.currentStage ? `Processing (${game.currentStage.replace(/_/g, ' ')})` : 'Downloading'}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ) : (
                            <>
                              {game.status === 'scheduled' && (
                                <Chip
                                  icon={<AccessTimeIcon sx={{ fontSize: '14px !important' }} />}
                                  label="Scheduled"
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                />
                              )}
                              {game.status === 'partial' && (
                                <Chip
                                  icon={<WarningAmberIcon sx={{ fontSize: '14px !important' }} />}
                                  label="Pano Retry Queued"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                              {game.status === 'overrun_delayed' && (
                                <Chip
                                  icon={<PendingIcon sx={{ fontSize: '14px !important' }} />}
                                  label="Overrun Delay"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                              {game.status === 'retrying_pano' && (
                                <Chip
                                  icon={<ReplayIcon sx={{ fontSize: '14px !important' }} />}
                                  label={`Retrying Pano (${game.panoAttempts || 1})`}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                              {game.status === 'retrying_auto' && (
                                <Chip
                                  icon={<ReplayIcon sx={{ fontSize: '14px !important' }} />}
                                  label="Retrying Auto"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                            </>
                          )}
                        </Box>

                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          vs {game.opponent}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                          <PlaceIcon sx={{ fontSize: 16 }} />
                          {game.rinkName} {game.sheetName && `(${game.sheetName})`}
                        </Typography>

                        {queuedScore && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1.5,
                              p: 0.75,
                              px: 1.25,
                              borderRadius: 1.5,
                              bgcolor: (theme) =>
                                theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Chip
                              label={queuedScore.outcomeLabel.toUpperCase()}
                              color={
                                queuedScore.outcome === 'W'
                                  ? 'success'
                                  : queuedScore.outcome === 'L'
                                  ? 'error'
                                  : 'warning'
                              }
                              size="small"
                              sx={{
                                fontWeight: 800,
                                fontSize: '0.72rem',
                                height: 22,
                                letterSpacing: 0.5,
                              }}
                            />
                            <Typography variant="body2" fontWeight={700} color="text.primary">
                              Final: {queuedScore.jetsScore} – {queuedScore.oppScore}
                              {queuedScore.isOvertime ? ' (OT)' : ''}
                            </Typography>
                          </Box>
                        )}

                        {isTaskRunning ? (
                          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(37, 99, 235, 0.1)', borderRadius: 1, border: '1px solid rgba(37, 99, 235, 0.3)' }}>
                            <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase' }}>
                              ⚡ Active Ingestion Task
                            </Typography>
                            <Typography variant="body2" color="primary.light" fontWeight={600} sx={{ mt: 0.25 }}>
                              Stage: {game.currentStage ? game.currentStage.replace(/_/g, ' ') : 'Running'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Live worker is actively downloading and processing stream from LiveBarn.
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                              {game.status === 'retrying_pano' || game.status === 'partial' ? 'NEXT PANO RETRY' : game.status === 'retrying_auto' ? 'NEXT AUTO RETRY' : game.status === 'overrun_delayed' ? 'OVERRUN RECHECK' : 'SCHEDULED DOWNLOAD'}
                            </Typography>
                            <Typography variant="body2" color="primary.main" fontWeight={500} sx={{ mt: 0.25 }}>
                              ⏰ {game.scheduledForFormatted || (game.scheduledExecutionTime ? new Date(game.scheduledExecutionTime).toLocaleString() : '30m after game end')}
                            </Typography>
                            {(game.status === 'retrying_pano' || game.status === 'partial') && (
                              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                Retrying panoramic full-ice camera feed from LiveBarn.
                              </Typography>
                            )}
                            {game.status === 'retrying_auto' && (
                              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                Retrying auto-tracking camera feed from LiveBarn.
                              </Typography>
                            )}
                            {game.status === 'overrun_delayed' && (
                              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                Waiting for game to conclude / flip to final before downloading.
                              </Typography>
                            )}
                          </Box>
                        )}
                      </CardContent>

                      <CardActions sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={
                            isBusy || isTaskRunning ? (
                              <CircularProgress size={16} />
                            ) : game.status === 'partial' || game.status === 'retrying_pano' ? (
                              <ReplayIcon />
                            ) : (
                              <ScheduleSendIcon />
                            )
                          }
                          onClick={() => (game.status === 'partial' || game.status === 'retrying_pano' ? handleRetryFeed(game.gameId, 'pano') : handleScheduleGame(game))}
                          disabled={Boolean(isBusy || isTaskRunning)}
                        >
                          {isBusy ? (
                            'Queueing...'
                          ) : isTaskRunning ? (
                            `In Progress (${game.currentStage ? game.currentStage.replace(/_/g, ' ') : 'processing'})`
                          ) : game.status === 'partial' || game.status === 'retrying_pano' ? (
                            'Retry Pano Now'
                          ) : (
                            'Trigger Download Now'
                          )}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<TerminalIcon />}
                          onClick={() => setSelectedLogGame({ gameId: game.gameId, opponent: game.opponent, date: game.date })}
                          sx={{ minWidth: '80px' }}
                        >
                          Logs
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

      {/* TAB 2: ADMIN INGESTION & TASK MONITOR */}
      {isUserAdmin && activeTab === 2 && (
        <Box>
          {/* Summary Metric Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderTop: '4px solid #2e7d32' }}>
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {summary?.readyCount ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  DOWNLOADED & READY
                </Typography>
              </Card>
            </Grid>

            <Grid size={{ xs: 6, sm: 3 }}>
              <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderTop: '4px solid #0288d1' }}>
                <Typography variant="h4" fontWeight={700} color="info.main">
                  {summary?.scheduledCount ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  SCHEDULED IN QUEUE
                </Typography>
              </Card>
            </Grid>

            <Grid size={{ xs: 6, sm: 3 }}>
              <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderTop: '4px solid #ed6c02' }}>
                <Typography variant="h4" fontWeight={700} color="warning.main">
                  {summary?.retryingCount ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  RETRYING PANO (+24H)
                </Typography>
              </Card>
            </Grid>

            <Grid size={{ xs: 6, sm: 3 }}>
              <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderTop: '4px solid #9e9e9e' }}>
                <Typography variant="h4" fontWeight={700} color="text.secondary">
                  {summary?.noFeedCount ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  NO LIVEBARN AT RINK
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Controls Bar */}
          <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Filter by opponent, rink name, or date..."
                  value={ingestionSearch}
                  onChange={(e) => setIngestionSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Filter Status"
                  value={ingestionFilter}
                  onChange={(e) => setIngestionFilter(e.target.value)}
                >
                  <MenuItem value="all">All Ingestion Items ({ingestionData?.games?.length || 0})</MenuItem>
                  <MenuItem value="scheduled">Scheduled in Queue ({summary?.scheduledCount || 0})</MenuItem>
                  <MenuItem value="ready">Ready ({summary?.readyCount || 0})</MenuItem>
                  <MenuItem value="retrying">Retrying Feed ({summary?.retryingCount || 0})</MenuItem>
                  <MenuItem value="no_feed">No LiveBarn Available ({summary?.noFeedCount || 0})</MenuItem>
                  <MenuItem value="not_scheduled">Not Scheduled ({summary?.notScheduledCount || 0})</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Sort Order"
                  value={ingestionSort}
                  onChange={(e) => setIngestionSort(e.target.value as any)}
                >
                  <MenuItem value="queue">Next to Run (Queue Order)</MenuItem>
                  <MenuItem value="newest">Game Date (Newest First)</MenuItem>
                  <MenuItem value="oldest">Game Date (Oldest First)</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Card>

          {/* Ingestion Table */}
          {ingestionLoading ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead sx={{ bgcolor: 'grey.100' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Game & Opponent</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Rink & Surface</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>LiveBarn Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Ingestion State</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Feeds</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <TableRow key={`table-skeleton-${idx}`}>
                      <TableCell>
                        <Skeleton variant="text" width="70%" height={20} />
                        <Skeleton variant="text" width="40%" height={16} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width="60%" height={20} />
                        <Skeleton variant="text" width="30%" height={16} />
                      </TableCell>
                      <TableCell><Skeleton variant="rounded" width={80} height={22} /></TableCell>
                      <TableCell><Skeleton variant="rounded" width={90} height={22} /></TableCell>
                      <TableCell><Skeleton variant="text" width="50%" height={20} /></TableCell>
                      <TableCell align="right"><Skeleton variant="rounded" width={70} height={30} sx={{ ml: 'auto' }} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : filteredIngestionGames.length === 0 ? (
            <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No ingestion games matching selected filters.
              </Typography>
            </Card>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead sx={{ bgcolor: 'grey.100' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Game & Opponent</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Rink & Surface</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>LiveBarn Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Ingestion State</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Feeds</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredIngestionGames.map((game) => {
                    const isBusy = actionInProgress === game.gameId;

                    return (
                      <TableRow key={game.gameId} hover>
                        {/* Game & Opponent */}
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {isManualGame(game) && game.targetTeam
                              ? `${game.targetTeam} vs ${game.opponent}`
                              : `vs ${game.opponent}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {game.date} • {game.scheduledStart}
                          </Typography>
                          <Chip
                            label={
                              isManualGame(game)
                                ? 'Manual GameSheet'
                                : game.source === 'tournament'
                                ? 'Tournament'
                                : 'CSDHL League'
                            }
                            color={isManualGame(game) ? 'secondary' : 'default'}
                            size="small"
                            sx={{ fontSize: '10px', height: 18, mt: 0.5 }}
                          />
                        </TableCell>

                        {/* Rink & Surface */}
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {game.rinkName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {game.sheetName || 'Main'}
                          </Typography>
                        </TableCell>

                        {/* LiveBarn Status */}
                        <TableCell>
                          {game.hasLivebarn ? (
                            <Tooltip title={game.livebarnUrl || ''}>
                              <Chip
                                label={`Surface #${game.surfaceId}`}
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ fontSize: '11px' }}
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title="This venue has no LiveBarn cameras installed.">
                              <Chip
                                icon={<WarningAmberIcon sx={{ fontSize: '13px !important' }} />}
                                label="No LiveBarn Feed"
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{ fontSize: '11px' }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>

                        {/* Ingestion State */}
                        <TableCell>
                          {game.status === 'ready' && (
                            <Chip
                              icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                              label="Downloaded & Ready"
                              color="success"
                              size="small"
                            />
                          )}

                          {game.status === 'scheduled' && (
                            <Box>
                              <Chip
                                icon={<AccessTimeIcon sx={{ fontSize: '14px !important' }} />}
                                label="Scheduled in Queue"
                                color="info"
                                size="small"
                              />
                              <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 0.5, fontWeight: 500 }}>
                                ⏰ {game.scheduledForFormatted || '30m after game end'}
                              </Typography>
                            </Box>
                          )}

                          {game.status === 'retrying_pano' && (
                            <Box>
                              <Chip
                                icon={<ReplayIcon sx={{ fontSize: '14px !important' }} />}
                                label={`Retrying Pano (Attempt ${game.panoAttempts || 1})`}
                                color="warning"
                                size="small"
                              />
                              {game.panoRetryNextAttempt && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  Next: {game.panoRetryNextAttempt.split('T')[0]}
                                </Typography>
                              )}
                            </Box>
                          )}

                          {game.status === 'retrying_auto' && (
                            <Box>
                              <Chip
                                icon={<ReplayIcon sx={{ fontSize: '14px !important' }} />}
                                label="Retrying Auto"
                                color="warning"
                                size="small"
                              />
                            </Box>
                          )}

                          {game.status === 'overrun_delayed' && (
                            <Chip
                              icon={<PendingIcon sx={{ fontSize: '14px !important' }} />}
                              label="Game Overrun Delayed"
                              color="warning"
                              size="small"
                            />
                          )}

                          {game.status === 'no_feed' && (
                            <Chip
                              label="Venue Not Supported"
                              size="small"
                              variant="outlined"
                              sx={{ color: 'text.disabled' }}
                            />
                          )}

                          {game.status === 'not_scheduled' && (
                            <Chip
                              label="Not Queued"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>

                        {/* Feeds */}
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            <Chip
                              label="Auto"
                              size="small"
                              color={game.feeds?.auto === 'downloaded' ? 'success' : game.status === 'retrying_auto' ? 'warning' : 'default'}
                              variant={game.feeds?.auto === 'downloaded' ? 'filled' : 'outlined'}
                              sx={{ fontSize: '10px', height: 20 }}
                            />
                            <Chip
                              label="Pano"
                              size="small"
                              color={game.feeds?.pano === 'downloaded' ? 'success' : game.status === 'retrying_pano' ? 'warning' : 'default'}
                              variant={game.feeds?.pano === 'downloaded' ? 'filled' : 'outlined'}
                              sx={{ fontSize: '10px', height: 20 }}
                            />
                          </Stack>
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {(game.status === 'ready' || game.status === 'partial' || game.status === 'complete') && (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<PlayCircleOutlineIcon />}
                                onClick={() => navigate(`/highlights/${game.gameId}`)}
                              >
                                Watch
                              </Button>
                            )}

                            {(game.status === 'retrying_pano' || ((game.status === 'partial' || game.status === 'ready' || game.status === 'failed') && game.feeds?.pano !== 'downloaded' && game.hasLivebarn)) && (
                              <Tooltip title="Force immediate retry of panoramic feed from LiveBarn">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={() => handleRetryFeed(game.gameId, 'pano')}
                                    disabled={isBusy}
                                  >
                                    {isBusy ? <CircularProgress size={16} /> : <ReplayIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}

                            {game.status === 'retrying_auto' && (
                              <Tooltip title="Force immediate re-check of auto-tracking feed">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={() => handleRetryFeed(game.gameId, 'auto')}
                                    disabled={isBusy}
                                  >
                                    {isBusy ? <CircularProgress size={16} /> : <ReplayIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}

                            {(game.status === 'not_scheduled' || game.status === 'scheduled') && game.hasLivebarn && (
                              <Tooltip title="Queue LiveBarn download via Cloud Tasks">
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={isBusy ? <CircularProgress size={14} /> : <ScheduleSendIcon />}
                                    onClick={() => handleScheduleGame(game)}
                                    disabled={isBusy}
                                  >
                                    {game.status === 'scheduled' ? 'Reschedule' : 'Queue'}
                                  </Button>
                                </span>
                              </Tooltip>
                            )}

                            <Tooltip title="View task execution logs & telemetry">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => setSelectedLogGame({ gameId: game.gameId, opponent: game.opponent, date: game.date })}
                              >
                                <TerminalIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* TAB 3: MANUAL GAMESHEET DOWNLOADS (Admins only) */}
      {isUserAdmin && activeTab === 3 && (
        <Box>
          <Card variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3, bgcolor: 'background.paper' }}>
            <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddCircleOutlineIcon color="primary" />
              Manual GameSheet Download
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Paste a GameSheet game link (for any team or season) to fetch the box score metadata, select a target team, and trigger the automated LiveBarn video download & processing pipeline. Manual downloads appear exclusively in this tab.
            </Typography>

            <Box component="form" onSubmit={handleLookupGamesheet}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 9 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="GameSheet Game URL"
                    placeholder="https://gamesheetstats.com/seasons/16126/games/3044950?tab=box-score"
                    value={manualUrlInput}
                    onChange={(e) => setManualUrlInput(e.target.value)}
                    disabled={lookupLoading || manualQueueing}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={lookupLoading || manualQueueing || !manualUrlInput.trim()}
                    startIcon={lookupLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                  >
                    {lookupLoading ? 'Looking up...' : 'Fetch Game'}
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {lookupError && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setLookupError(null)}>
                {lookupError}
              </Alert>
            )}

            {lookupResult && (
              <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Grid container spacing={3}>
                  {/* Left column: Game Metadata & Target Team Selection */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
                      1. Game Details & Target Team
                    </Typography>
                    <Card variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarMonthIcon sx={{ fontSize: 15 }} />
                          {lookupResult.date} • {lookupResult.scheduledStart}
                        </Typography>
                        <Chip
                          label={lookupResult.status ? lookupResult.status.toUpperCase() : 'SCHEDULED'}
                          size="small"
                          color={lookupResult.status === 'final' ? 'success' : 'info'}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }}
                        />
                      </Stack>

                      <Typography variant="subtitle1" fontWeight={700}>
                        {lookupResult.visitorTeam.name} @ {lookupResult.homeTeam.name}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <PlaceIcon sx={{ fontSize: 16 }} />
                        {lookupResult.location || 'Unknown Location'}
                        {lookupResult.divisionName ? ` • ${lookupResult.divisionName}` : ''}
                      </Typography>

                      {lookupResult.finalScore &&
                        lookupResult.finalScore.visitorGoals !== undefined &&
                        lookupResult.finalScore.homeGoals !== undefined && (
                          <Typography variant="body2" fontWeight={700} sx={{ mt: 1 }}>
                            Score: {lookupResult.visitorTeam.name} {lookupResult.finalScore.visitorGoals} – {lookupResult.homeTeam.name} {lookupResult.finalScore.homeGoals}
                            {lookupResult.isOvertime ? ' (OT)' : ''}
                          </Typography>
                        )}

                      <Divider sx={{ my: 1.5 }} />

                      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        SELECT TARGET TEAM (Win/Loss badge & card perspective):
                      </Typography>
                      <RadioGroup
                        value={selectedTargetTeam}
                        onChange={(e) => setSelectedTargetTeam(e.target.value)}
                      >
                        <FormControlLabel
                          value={lookupResult.homeTeam.name}
                          control={<Radio size="small" />}
                          label={
                            <Typography variant="body2" fontWeight={selectedTargetTeam === lookupResult.homeTeam.name ? 700 : 400}>
                              {lookupResult.homeTeam.name} (Home)
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          value={lookupResult.visitorTeam.name}
                          control={<Radio size="small" />}
                          label={
                            <Typography variant="body2" fontWeight={selectedTargetTeam === lookupResult.visitorTeam.name ? 700 : 400}>
                              {lookupResult.visitorTeam.name} (Visitor)
                            </Typography>
                          }
                        />
                      </RadioGroup>
                    </Card>
                  </Grid>

                  {/* Right column: LiveBarn Surface Resolution / Override */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
                      2. LiveBarn Rink & Surface ID
                    </Typography>

                    {lookupResult.livebarn?.resolved && !showRinkOverride ? (
                      <Alert
                        severity="success"
                        sx={{ mb: 2 }}
                        action={
                          <Button color="inherit" size="small" onClick={() => setShowRinkOverride(true)}>
                            Override
                          </Button>
                        }
                      >
                        <Typography variant="body2" fontWeight={700}>
                          LiveBarn Surface Resolved: #{lookupResult.livebarn.surfaceId}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          {lookupResult.livebarn.rinkName} ({lookupResult.livebarn.sheetName || 'Main'})
                        </Typography>
                      </Alert>
                    ) : (
                      <Box>
                        {!lookupResult.livebarn?.resolved && (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight={700}>
                              Unknown LiveBarn Rink ID for &ldquo;{lookupResult.location || 'this venue'}&rdquo;
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              Select a known LiveBarn surface below or enter a LiveBarn Surface ID / Watch URL manually.
                            </Typography>
                          </Alert>
                        )}

                        <Stack spacing={1.5}>
                          {lookupResult.knownRinks && lookupResult.knownRinks.length > 0 && (
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="Quick-Fill from Known LiveBarn Rinks"
                              value={
                                lookupResult.knownRinks.some((r) => String(r.surfaceId) === manualSurfaceInput)
                                  ? manualSurfaceInput
                                  : ''
                              }
                              onChange={(e) => {
                                const chosenId = String(e.target.value);
                                setManualSurfaceInput(chosenId);
                                const found = lookupResult.knownRinks.find((r) => String(r.surfaceId) === chosenId);
                                if (found) {
                                  setManualRinkNameInput(found.rinkName);
                                  setManualSheetNameInput(found.sheetName);
                                }
                              }}
                            >
                              <MenuItem value="">
                                <em>-- Custom Surface ID / URL --</em>
                              </MenuItem>
                              {lookupResult.knownRinks.map((rink) => (
                                <MenuItem key={`${rink.surfaceId}-${rink.sheetName}`} value={String(rink.surfaceId)}>
                                  {rink.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}

                          <TextField
                            fullWidth
                            size="small"
                            required
                            label="LiveBarn Surface ID or URL"
                            placeholder="e.g. 771 or https://watch.livebarn.com/en/video/771"
                            value={manualSurfaceInput}
                            onChange={(e) => setManualSurfaceInput(e.target.value)}
                          />

                          <Grid container spacing={1.5}>
                            <Grid size={{ xs: 12, sm: 7 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Rink Display Name"
                                placeholder="e.g. Fifth Third Arena"
                                value={manualRinkNameInput}
                                onChange={(e) => setManualRinkNameInput(e.target.value)}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 5 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Sheet Name"
                                placeholder="e.g. Blackhawks"
                                value={manualSheetNameInput}
                                onChange={(e) => setManualSheetNameInput(e.target.value)}
                              />
                            </Grid>
                          </Grid>

                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={saveRinkMapping}
                                onChange={(e) => setSaveRinkMapping(e.target.checked)}
                              />
                            }
                            label={
                              <Typography variant="caption" color="text.secondary">
                                Save mapping for &ldquo;{lookupResult.location}&rdquo; for future games
                              </Typography>
                            }
                          />
                        </Stack>
                      </Box>
                    )}

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                      <Button
                        variant="outlined"
                        color="inherit"
                        onClick={() => {
                          setLookupResult(null);
                          setLookupError(null);
                        }}
                        disabled={manualQueueing}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={manualQueueing ? <CircularProgress size={16} color="inherit" /> : <CloudDownloadIcon />}
                        onClick={handleTriggerManualDownload}
                        disabled={manualQueueing || !selectedTargetTeam}
                      >
                        {manualQueueing ? 'Queueing Download...' : 'Download & Process Game'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Card>

          {/* Downloaded & Pending Manual Games */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Downloaded Manual Games ({allManualGames.length})
            </Typography>
            {allManualGames.some((g) => g.status === 'processing' || g.lastTaskStatus === 'running' || g.currentStage === 'queued') && (
              <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 600 }}>
                <CircularProgress size={12} />
                Auto-updating status...
              </Typography>
            )}
          </Box>

          {allManualGames.length === 0 ? (
            <Card variant="outlined" sx={{ p: 5, textAlign: 'center' }}>
              <SportsHockeyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="h6" color="text.secondary">
                No manual GameSheet downloads yet.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Paste a GameSheet link above to fetch a game and trigger a manual download.
              </Typography>
            </Card>
          ) : (
            <Grid container spacing={3}>
              {allManualGames.map((video) => {
                const gameId = video.gameId || video.id;
                const hasAuto = video.feeds?.auto?.status === 'downloaded';
                const hasPano = video.feeds?.pano?.status === 'downloaded';
                const isWatchable = hasAuto || hasPano;
                const isBusy = actionInProgress === gameId;
                const isTaskRunning = video.status === 'processing' || video.lastTaskStatus === 'running';
                const isPendingJob =
                  isTaskRunning ||
                  video.status === 'scheduled' ||
                  video.currentStage === 'queued' ||
                  video.status === 'retrying_pano' ||
                  video.status === 'retrying_auto';
                const isWaitingForStream = video.status === 'waiting_for_stream' || video.status === 'overrun_delayed';
                const isFailed = video.status === 'failed' || video.lastTaskStatus === 'failed';
                const goalsCount = video.goals?.length || video.highlights?.length || 0;
                const scoreResult = resolveGameScore(video, []);
                const focalTeam = getTargetTeamName(video, false);
                const isHome = isJetsHomeGame(video, null, null);
                const stageLabel = formatManualStageLabel(video.currentStage, video.status);

                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={gameId}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderStyle: !isWatchable && isPendingJob ? 'dashed' : 'solid',
                        borderColor: !isWatchable && isTaskRunning ? 'primary.main' : 'divider',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                        },
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarMonthIcon sx={{ fontSize: 16 }} />
                            {video.date} • {video.scheduledStart}
                          </Typography>

                          {isTaskRunning ? (
                            <Chip
                              icon={<CircularProgress size={12} />}
                              label={video.currentStage === 'queued' ? 'Job Submitted • Pending' : 'Processing...'}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ) : isWatchable ? (
                            <Chip
                              icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                              label={hasAuto && hasPano ? 'Full Replay' : hasAuto ? 'Auto Feed' : 'Pano Feed'}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          ) : isFailed ? (
                            <Chip
                              icon={<WarningAmberIcon sx={{ fontSize: '14px !important' }} />}
                              label="Failed"
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          ) : isWaitingForStream ? (
                            <Chip
                              icon={<AccessTimeIcon sx={{ fontSize: '14px !important' }} />}
                              label="Waiting for VOD"
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              icon={<AccessTimeIcon sx={{ fontSize: '14px !important' }} />}
                              label="Job Submitted • Pending"
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                          )}
                        </Box>

                        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ lineHeight: 1.25 }}>
                          {focalTeam} {isHome ? 'vs' : '@'} {video.opponent}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                          <PlaceIcon sx={{ fontSize: 16 }} />
                          {video.rinkName} {video.sheetName && `(${video.sheetName})`}
                        </Typography>

                        {scoreResult && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1.5,
                              p: 0.75,
                              px: 1.25,
                              borderRadius: 1.5,
                              bgcolor: (theme) =>
                                theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Chip
                              label={scoreResult.outcomeLabel.toUpperCase()}
                              color={
                                scoreResult.outcome === 'W'
                                  ? 'success'
                                  : scoreResult.outcome === 'L'
                                  ? 'error'
                                  : 'warning'
                              }
                              size="small"
                              sx={{
                                fontWeight: 800,
                                fontSize: '0.72rem',
                                height: 22,
                                letterSpacing: 0.5,
                              }}
                            />
                            <Typography variant="body2" fontWeight={700} color="text.primary">
                              Final: {scoreResult.jetsScore} – {scoreResult.oppScore}
                              {scoreResult.isOvertime ? ' (OT)' : ''}
                            </Typography>
                          </Box>
                        )}

                        {(!isWatchable || isTaskRunning) && (
                          <Box
                            sx={{
                              mt: 1,
                              mb: 1.5,
                              p: 1.25,
                              bgcolor: 'action.hover',
                              borderRadius: 1.5,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            {(isTaskRunning || (isPendingJob && !isWatchable)) && (
                              <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />
                            )}
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              color={isFailed ? 'error.main' : isWaitingForStream ? 'warning.main' : 'primary.main'}
                              sx={{ display: 'block' }}
                            >
                              {stageLabel}
                            </Typography>
                            {(video.lastTaskError || video.delayReason) ? (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {video.lastTaskError || video.delayReason}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {isTaskRunning
                                  ? 'Status updates automatically while on this page...'
                                  : video.scheduledForFormatted
                                  ? `Scheduled: ${video.scheduledForFormatted}`
                                  : 'Queued for background worker...'}
                              </Typography>
                            )}
                          </Box>
                        )}

                        <Stack direction="row" spacing={1} sx={{ mt: 'auto', pt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                          <Chip label={`Target: ${focalTeam}`} size="small" color="secondary" variant="outlined" />
                          {hasAuto ? (
                            <Chip label="1080p Auto" size="small" variant="filled" />
                          ) : (
                            <Chip label="Auto: Pending" size="small" variant="outlined" />
                          )}
                          {hasPano ? (
                            <Chip label="Panoramic" size="small" variant="filled" />
                          ) : (
                            <Chip label="Pano: Pending" size="small" variant="outlined" />
                          )}
                          {goalsCount > 0 && (
                            <Chip
                              label={`${goalsCount} Goal${goalsCount > 1 ? 's' : ''}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      </CardContent>

                      <CardActions sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                        {isWatchable ? (
                          <Button
                            fullWidth
                            variant="contained"
                            startIcon={<PlayCircleOutlineIcon />}
                            onClick={() => navigate(`/highlights/${gameId}`)}
                          >
                            Watch Game & Clips
                          </Button>
                        ) : isTaskRunning || isBusy ? (
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<CircularProgress size={16} />}
                            disabled
                          >
                            {isBusy ? 'Queueing...' : 'Processing Pending...'}
                          </Button>
                        ) : (
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<ScheduleSendIcon />}
                            onClick={() => handleScheduleGame(video as any)}
                          >
                            Retry Download Now
                          </Button>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<TerminalIcon />}
                          onClick={() => setSelectedLogGame({ gameId, opponent: video.opponent, date: video.date })}
                          sx={{ minWidth: '80px' }}
                        >
                          Logs
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

      {selectedLogGame && (
        <TaskDebugLogsModal
          isOpen={Boolean(selectedLogGame)}
          onClose={() => setSelectedLogGame(null)}
          gameId={selectedLogGame.gameId}
          opponent={selectedLogGame.opponent}
          date={selectedLogGame.date}
        />
      )}

      {/* Action Toast Feedback */}
      {actionToast && (
        <Snackbar
          open={Boolean(actionToast)}
          autoHideDuration={actionToast.severity === 'info' ? 10000 : 6000}
          onClose={() => setActionToast(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={actionToast.severity}
            onClose={() => setActionToast(null)}
            variant="filled"
            sx={{ width: '100%', boxShadow: 4, fontWeight: 500 }}
          >
            {actionToast.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default GameHighlightsListPage;
